// Local calendar dates avoid UTC offsets moving a lesson to another day.
export function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function localDate(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
}
export function moveDate(value, days) {
  const date = localDate(value);
  date.setDate(date.getDate() + days);
  return dateKey(date);
}
export function calendarDays(value, mode) {
  const date = localDate(value);
  if (mode === 'month') date.setDate(1);
  date.setDate(date.getDate() - (date.getDay() + 6) % 7);
  let count = 7;
  if (mode === 'month') {
    const anchor = localDate(value);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 12);
    // Count civil days, independent of daylight saving changes.
    count = 0;
    const cursor = new Date(date);
    while (cursor <= last || count % 7 !== 0) { count++; cursor.setDate(cursor.getDate() + 1); }
  }
  return Array.from({ length: count }, (_, index) => moveDate(dateKey(date), index));
}
export function timeMinutes(value) {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value || ''));
  if (!match) return null;
  const hour = Number(match[1]), minute = Number(match[2]);
  return hour < 24 && minute < 60 ? hour * 60 + minute : null;
}
export function lessonInterval(record) {
  const start = timeMinutes(record.timeFrom), end = timeMinutes(record.timeTo);
  if (start === null || end === null || start === end) return null;
  return { start, end: end < start ? end + 1440 : end };
}
export function sortLessons(records) {
  return [...records].sort((a, b) => (timeMinutes(a.timeFrom) ?? 3000) - (timeMinutes(b.timeFrom) ?? 3000) || String(a.id).localeCompare(String(b.id)));
}
// Each hour is one shared row across the week. Cards flow vertically within a
// day; row height follows the busiest day instead of hiding overlapping lessons.
export function weeklyLessonRows(days, records) {
  const buckets = days.map(date => {
    const hours = {};
    for (const record of sortLessons(records[date] || [])) {
      const interval = lessonInterval(record);
      if (!interval) continue;
      const hour = Math.floor(interval.start / 60);
      if (!hours[hour]) hours[hour] = [];
      hours[hour].push(record);
    }
    return hours;
  });
  const activeHours = buckets.flatMap(hours => Object.keys(hours).map(Number));
  const first = Math.min(8, ...activeHours), last = Math.max(19, ...activeHours);
  return Array.from({length:last-first+1}, (_, index) => {
    const hour = first + index;
    return {hour, days:buckets.map(hours => hours[hour] || [])};
  });
}
// 84px/hour; short lessons retain a clickable 28px area. Packing uses those
// visible bounds as well so even adjacent short lessons cannot cover each other.
export function layoutLessons(records) {
  const items = records.map(record => ({ record, interval: lessonInterval(record) })).filter(item => item.interval)
    .map(item => ({ ...item, start: item.interval.start, end: item.interval.end, visualEnd: Math.max(item.interval.end, item.interval.start + 20) }))
    .sort((a, b) => a.start - b.start || b.end - a.end || String(a.record.id).localeCompare(String(b.record.id)));
  const groups = [];
  for (const item of items) {
    let group = groups[groups.length - 1];
    if (!group || item.start >= group.end) {
      group = { start: item.start, end: item.visualEnd, items: [], lanes: [] };
      groups.push(group);
    }
    let lane = group.lanes.findIndex(end => end <= item.start);
    if (lane < 0) lane = group.lanes.length;
    group.lanes[lane] = item.visualEnd;
    group.end = Math.max(group.end, item.visualEnd);
    group.items.push({ ...item, lane });
  }
  return groups.map(group => ({ ...group, laneCount: group.lanes.length, collapsed: group.lanes.length > 2 }));
}
